import { useNavigate } from 'react-router';

import PhotoFilter from '@/components/PhotoFilter';
import { usePhotos } from '@/hooks/usePhotos';
import { useSearchParamsWithUpdate } from '@/hooks/useSearchParamsWithUpdate';
import { Photo } from '@/lib/Photo';
import { Photos } from '@/lib/Photos';
import { shortDateFormatter } from '@/lib/utils';

export const PhotoGridPane = ({ onPhotoClick, mode = 'grid' }: {
  onPhotoClick?: (photo: Photo) => void;
  mode?: 'grid' | 'filter' | 'calendar';
}) => {
  const { photos: photoData } = usePhotos();
  const navigate = useNavigate();

  // Convert PhotoData to Photo instances
  const photoInstances = photoData.map(data => Photo.fromApi(data));
  const photos = new Photos(photoInstances);

  const handlePhotoClick = (photo: Photo) => {
    if (onPhotoClick) {
      onPhotoClick(photo);
    } else {
      navigate(photo.url());
    }
  }

  const stringDateDescending = (a: string, b: string) => new Date(b).getTime() - new Date(a).getTime()
  const photoDateDescending = (a: Photo, b: Photo) => b.date.getTime() - a.date.getTime()

  const { getMany } = useSearchParamsWithUpdate();

  const [state, province, region, type] = getMany(['state', 'province', 'region', 'type'], '');

  const filteredPhotos = photos.filterBy(state, province, region, type);

  return (
    <>
      {mode === 'filter' && <PhotoFilter />}
      {mode === 'calendar' ? (
        <>
          {Object.keys(photos.groupedByDate)
            .sort(stringDateDescending)
            .map((date, index) => (
              <div key={date} className={`flex flex-col my-8`}>
                <h2 className={`mx-4 text-2xl italic font-bold text-${index % 2 === 0 ? 'left' : 'right'}`}>
                  {shortDateFormatter(new Date(date))}
                </h2>
                <PhotoTiles
                  photos={photos.groupedByDate[date].sort(photoDateDescending)}
                  handlePhotoClick={handlePhotoClick}
                  justify={index % 2 === 0 ? 'start' : 'end'} />
              </div>
            ))}
        </>
      ) : (
        <PhotoTiles photos={filteredPhotos} handlePhotoClick={handlePhotoClick} />
      )
      }
    </>
  );
};

const PhotoTiles = ({ photos, handlePhotoClick, justify = 'start' }: {
  photos: Photo[],
  handlePhotoClick: (photo: Photo) => void,
  justify?: 'start' | 'end'
}) => {
  return (
    <div className={`flex flex-wrap items-center justify-${justify || 'start'} w-full`}>
      {photos.map((photo) => (
        <PhotoTile key={photo.id} photo={photo} handlePhotoClick={handlePhotoClick} />
      ))}
    </div>
  );
};

const PhotoTile = ({ photo, handlePhotoClick }: {
  photo: Photo,
  handlePhotoClick: (photo: Photo) => void
}) => {
  return (
    <div
      key={photo.id}
      className="relative w-1/4 p-1 overflow-hidden transition-transform duration-200 rounded-sm shadow-md cursor-pointer group aspect-square hover:scale-105"
      onClick={() => handlePhotoClick(photo)}
    >
      <img
        src={photo.thumbUrl || photo.imageUrl}
        alt={photo.title}
        className="object-cover w-full h-full"
      />
      <div
        className="absolute bottom-0 left-0 right-0 p-2 text-white transition-opacity duration-200 opacity-0 group-hover:opacity-100"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      >
        <p className="text-sm truncate">{photo.title}</p>
      </div>
    </div>
  );
};

export default PhotoGridPane;
